import { invalidateDocumentListData } from "@prismicio/editor-fields"
import { useInterval, useTabFocus } from "@prismicio/editor-support/React"
import { pluralize } from "@prismicio/editor-support/String"
import { Box, Toast } from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  invalidateReleaseSizeData,
  invalidateReleasesListData,
} from "../../app/services/release"
import {
  createArchiveTask,
  createCopyToLocaleTask,
  createDeleteTask,
  createPublishTask,
  getAllOngoingTasks,
  getTask,
  type TaskAction,
  type TaskRecord,
} from "./bulkActionsService"
import { Countdown } from "./Countdown"

interface BulkActionsContext {
  action: <T extends TaskAction>(
    action: T,
    ...args: Parameters<(typeof bulkActionsMap)[T]>
  ) => void
  hasBulkActionInProgress: boolean
  documentsInQueue: TaskRecord["items"][number][]
  queuedTasks: QueuedTask[]
  addOnGoingTask: (taskId: string) => void
}

export const BulkActionsContext = createContext<BulkActionsContext | undefined>(
  undefined,
)

interface BulkActionsProviderProps {
  children: ReactNode
  /**
   * Cleanup function that will run after each finished task
   */
  onCompletion?: (task: TaskRecord) => void
}

export function BulkActionsProvider(props: BulkActionsProviderProps) {
  const { children, onCompletion } = props

  const queryClient = useQueryClient()
  const [queuedTasks, setQueuedTasks] = useState<QueuedTaskMap>(new Map())
  const [isPolling, setIsPolling] = useState(false)

  const hasBulkActionInProgress = queuedTasks.size > 0

  const documentsInQueue = useMemo(() => {
    return Array.from(queuedTasks.values()).flatMap((taskRecord) => {
      if (taskRecord?.items === undefined) return []
      return taskRecord.items
    })
  }, [queuedTasks])

  const queuedTasksList = useMemo(() => {
    return Array.from(queuedTasks.values()).filter(
      (value): value is QueuedTask => value !== undefined,
    )
  }, [queuedTasks])

  const updateQueuedTasks = useCallback(
    (taskId: string, newTask: QueuedTask | undefined) => {
      setQueuedTasks((prev) => {
        const newTasks = new Map(prev)
        newTasks.set(taskId, newTask)

        return newTasks
      })
    },
    [setQueuedTasks],
  )

  const removeQueuedTask = useCallback(
    (taskId: string) => {
      setQueuedTasks((prev) => {
        const newTasks = new Map(prev)
        newTasks.delete(taskId)

        return newTasks
      })
    },
    [setQueuedTasks],
  )

  const removeTaskAfterDelay = useCallback(
    (taskId: string) => {
      setIsPolling(true)
      setTimeout(() => {
        removeQueuedTask(taskId)
      }, toastCleanupDelay) // TODO: When we change the toast library the toast should be closable by adding a close delay
    },
    [setIsPolling, removeQueuedTask],
  )

  function addOnGoingTask(taskId: string, localId?: string) {
    handleTask(taskId, localId)
  }

  function action<T extends TaskAction>(
    action: T,
    ...args: Parameters<(typeof bulkActionsMap)[T]>
  ) {
    setIsPolling(false)

    // Create a local ID to track manually triggered tasks before server creation
    const localId = crypto.randomUUID()
    const taskRecord: QueuedTask = {
      status: "idle",
      action,
      items: args[0]?.items,
    }

    const promise = bulkActionsMap[action]
    const timeoutId = setTimeout(() => {
      // @ts-expect-error - TypeScript can't infer the dynamic arguments for the promise
      promise(...args)
        .then((response) => handleTask(response.taskId, localId))
        .catch(() => {
          updateQueuedTasks(localId, {
            ...taskRecord,
            status: "error",
          })

          removeTaskAfterDelay(localId)
        })
    }, cancelDelaySeconds * 1_000)

    // Add timeoutId to task in queue, so we can cancel the mutation if needed
    updateQueuedTasks(localId, {
      ...taskRecord,
      timeoutId,
    })
  }

  // Query the server continuously to check if the task is complete
  // If the task is complete, show success toast and remove it from the queue
  // If the task is not complete, show loading toast and check again in 5 seconds
  // If the task fails, show error toast and remove it from the queue
  const handleTask = useCallback(
    (taskId: string, localId?: string) => {
      setIsPolling(false)

      // localId must take precedence over taskId if both are provided
      // localeId is our stable reference to manually triggered tasks
      const queueId = localId ?? taskId

      getTask(taskId)
        .then(async (taskRecord) => {
          // task has completed with no errors
          if (
            taskRecord.isComplete === true &&
            taskRecord.failed.length === 0
          ) {
            if (taskRecord.releaseId) {
              void invalidateReleaseSizeData(taskRecord.releaseId)
            }

            // Update the releases list if the task action is copy to another locale
            // to ensure that the new/updated release is visible in the releases list
            if (
              taskRecord.action === "copyToAnotherLocale" &&
              taskRecord.commitData?.releaseToCommitId
            ) {
              void invalidateReleasesListData()
            }

            await invalidateDocumentListData(queryClient)

            updateQueuedTasks(queueId, {
              status: "success",
              ...taskRecord,
            })

            onCompletion?.(taskRecord)
            removeTaskAfterDelay(queueId)
            return
          }

          // task has completed with errors
          if (taskRecord.isComplete === true && taskRecord.failed.length > 0) {
            if (taskRecord.releaseId) {
              void invalidateReleaseSizeData(taskRecord.releaseId)
            }

            await invalidateDocumentListData(queryClient)

            updateQueuedTasks(queueId, {
              status: "error",
              ...taskRecord,
            })

            onCompletion?.(taskRecord)
            removeTaskAfterDelay(queueId)
            return
          }

          // task is not finished and hasn't hit a fatal error
          // continue polling the task data
          if (taskRecord.isComplete === false && !taskRecord.fatalError) {
            updateQueuedTasks(queueId, {
              status: taskRecord.executionData ? "loading" : "queued",
              ...taskRecord,
            })

            // avoid over polling the task
            setTimeout(() => handleTask(taskId, localId), taskRetryDelay)
            return
          }

          // task has failed
          updateQueuedTasks(queueId, {
            status: "error",
            ...taskRecord,
          })

          removeTaskAfterDelay(queueId)
        })
        .catch(() => {
          removeTaskAfterDelay(queueId)
        })
    },
    [removeTaskAfterDelay, updateQueuedTasks, queryClient, onCompletion],
  )

  // Query the server for all ongoing tasks
  // If there are no ongoing tasks, start polling by an interval
  // If there are ongoing tasks, offload them to the task handler function
  const handleOngoingTasks = useCallback(() => {
    getAllOngoingTasks()
      .then((taskIds) => {
        setIsPolling(taskIds.length === 0)
        taskIds.forEach((taskId) => {
          setQueuedTasks((tasks) => tasks.set(taskId, undefined))
          handleTask(taskId)
        })
      })
      .catch(() => {
        // Ignore and try again later.
      })
  }, [handleTask])

  // check if there are any ongoing tasks on page load
  useEffect(handleOngoingTasks, [handleOngoingTasks])
  useInterval(handleOngoingTasks, isPolling ? pollingInterval : null)
  useTabFocus(handleOngoingTasks)

  function handleCancel(timeoutId: NodeJS.Timeout | undefined, taskId: string) {
    if (!timeoutId) return

    clearTimeout(timeoutId)
    removeQueuedTask(taskId)
  }

  return (
    <BulkActionsContext.Provider
      value={{
        action,
        hasBulkActionInProgress,
        documentsInQueue,
        addOnGoingTask,
        queuedTasks: queuedTasksList,
      }}
    >
      {children}

      {/* TODO: handle this with new toast approach */}
      {Array.from(queuedTasks.entries()).map(([taskId, taskRecord], index) => {
        if (taskRecord === undefined) return null

        const position = (index + 1) * 72
        const activeAction = taskRecord.action
        const documentCount = taskRecord.items?.length ?? 0
        const status = taskRecord.status
        const errors = taskRecord.failed ?? []

        const isLoading = status === "loading" || status === "queued"
        const successIcon = status === "success" ? "check" : undefined
        const errorIcon = status === "error" ? "alert" : undefined
        const icon = successIcon ?? errorIcon

        return (
          <Toast
            key={taskId}
            title={getTitle({ activeAction, documentCount, status, errors })}
            anchor={
              <Box
                position="sticky"
                width="100%"
                justifyContent="center"
                bottom={position}
              />
            }
            loadingIndicator={isLoading}
            icon={icon}
            open
            action={
              taskRecord.status === "idle"
                ? {
                    onClick: () => handleCancel(taskRecord.timeoutId, taskId),
                    title: "Cancel",
                  }
                : undefined
            }
          />
        )
      })}
    </BulkActionsContext.Provider>
  )
}

export function useBulkAction() {
  const context = useContext(BulkActionsContext)

  if (context === undefined) {
    throw new Error("useBulkAction must be used within a BulkActionsProvider")
  }

  return context
}

type ToastStatus = "idle" | "loading" | "success" | "error" | "queued"
type QueuedTask = Partial<TaskRecord> & {
  status: ToastStatus
  timeoutId?: ReturnType<typeof setTimeout>
}

type QueuedTaskMap = Map<TaskId, QueuedTask | undefined>
type TaskId = string

const cancelDelaySeconds = 5
const pollingInterval = 10_000
const taskRetryDelay = 5_000
const toastCleanupDelay = 5_000

const bulkActionsMap = {
  publish: createPublishTask,
  archive: createArchiveTask,
  delete: createDeleteTask,
  copyToAnotherLocale: createCopyToLocaleTask,
} as const

const bulkActionDictionary = {
  publish: "published",
  archive: "archived",
  delete: "deleted",
  copyToAnotherLocale: "copied",
} as const

interface GetTitleArgs {
  activeAction: TaskAction | undefined
  documentCount: number
  status: ToastStatus
  errors: TaskRecord["failed"]
}

function getTitle(args: GetTitleArgs) {
  const { activeAction, documentCount, status, errors } = args

  if (activeAction === undefined) return ""

  const activeActionLabel = bulkActionDictionary[activeAction]
  const type = activeAction === "copyToAnotherLocale" ? "version" : "document"
  const documentCountLabel = `${documentCount} ${pluralize(
    documentCount,
    type,
  )}`

  if (status === "loading")
    return `${documentCountLabel} currently being ${activeActionLabel}`
  if (status === "success")
    return `${documentCountLabel} successfully ${activeActionLabel}`
  if (status === "queued")
    return `${documentCountLabel} queued to be ${activeActionLabel}`
  if (status === "error") {
    // we need a diff to determine if everything failed or
    // if it was a partial failure
    const difference = documentCount - errors.length
    const hasPartiallyErrored = errors.length > 0 && difference > 0

    const differenceCountLabel = `${difference} ${pluralize(
      difference,
      "document was",
      "documents were",
    )}`

    return hasPartiallyErrored
      ? `${differenceCountLabel} ${activeActionLabel}, ${errors.length} failed to be ${activeActionLabel}`
      : `${documentCountLabel} failed being ${activeActionLabel}`
  }

  return (
    <Box gap={4} as="span">
      {documentCountLabel} will be {activeActionLabel} in
      <Countdown countdown={cancelDelaySeconds} />
    </Box>
  )
}
